import React, { useState, useMemo, useEffect } from "react";
import {
  useGetGeminiValidationLogsQuery,
  useGetGeminiValidationLogStatsQuery,
} from "apis/geminiValidationLog";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";

const formatLocalDateTime = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const VALIDATION_TYPES = [
  { value: "", label: "All Types" },
  { value: "photoEdit", label: "Photo Edit" },
  { value: "floorplan", label: "Floorplan" },
];

const STATUSES = [
  { value: "", label: "All Statuses" },
  { value: "PASS", label: "Pass" },
  { value: "FAIL", label: "Fail" },
  { value: "PARSE_ERROR", label: "Parse Error" },
  { value: "ERROR", label: "Error" },
];

const VALID_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Valid" },
  { value: "false", label: "Invalid" },
];

const REVISION_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Revisions only" },
  { value: "false", label: "Originals only" },
];

const TIME_FRAMES = [
  { value: "", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "lastWeek", label: "Last 7 Days" },
  { value: "lastMonth", label: "Last 30 Days" },
  { value: "last3Months", label: "Last 3 Months" },
  { value: "last6Months", label: "Last 6 Months" },
  { value: "custom", label: "Custom Range" },
];

const STATUS_CLASSES = {
  PASS: "bg-green-100 text-green-800",
  FAIL: "bg-red-100 text-red-800",
  PARSE_ERROR: "bg-orange-100 text-orange-800",
  ERROR: "bg-red-100 text-red-900",
};

const initialFilters = {
  validationType: "",
  status: "",
  valid: "",
  isRevision: "",
  category: "",
  model: "",
  search: "",
  timeFrame: "",
  startDate: "",
  endDate: "",
};

const PAGE_SIZE = 25;

const getOption = (options, value) =>
  options.find((o) => o.value === value) || null;

const formatSsim = (ssim) => {
  if (ssim === null || ssim === undefined || Number.isNaN(Number(ssim))) {
    return "-";
  }
  return `${(Number(ssim) * 100).toFixed(1)}%`;
};

const GeminiValidationLogs = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  const queryParams = useMemo(() => {
    const params = { page: currentPage, limit: PAGE_SIZE };
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        if (key === "timeFrame" && value === "custom") return;
        params[key] = value;
      }
    });
    return params;
  }, [appliedFilters, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  const { data: logs, pagination, isPending, isFetching } =
    useGetGeminiValidationLogsQuery(queryParams);
  const { data: stats } = useGetGeminiValidationLogStatsQuery(queryParams);

  const isCustomRange = filters.timeFrame === "custom";

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-xs whitespace-nowrap">
            {formatLocalDateTime(row.createdAt)}
          </span>
        ),
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "validationType",
        header: "Type",
        cell: (row) => (
          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800 capitalize">
            {row.validationType}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              STATUS_CLASSES[row.status] || "bg-gray-100 text-gray-800"
            }`}
          >
            {row.status}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "valid",
        header: "Valid",
        cell: (row) => (
          <span className={row.valid ? "text-green-700" : "text-red-700"}>
            {row.valid ? "Yes" : "No"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "model",
        header: "Model",
        cell: (row) => (
          <span className="text-xs">{row.model || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "ssim",
        header: "SSIM",
        cell: (row) => (
          <span className="text-xs whitespace-nowrap">{formatSsim(row.ssim)}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "visionCalls",
        header: "Vision",
        cell: (row) => (
          <span className="text-xs">
            {row.visionCalls === null || row.visionCalls === undefined
              ? "-"
              : row.visionCalls}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "roomType",
        header: "Room",
        cell: (row) => (
          <span className="text-xs">{row.roomType || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "propertyAddress",
        header: "Property",
        cell: (row) => (
          <span>
            {row.propertyAddress || row.propertyId?.address || "-"}
          </span>
        ),
        isFilter: false,
        maxWidth: "260px",
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: (row) => row.category || "-",
        isFilter: false,
      },
      {
        accessorKey: "durationMs",
        header: "Duration",
        cell: (row) => (row.durationMs ? `${row.durationMs} ms` : "-"),
        isFilter: false,
      },
    ],
    [],
  );

  if (isPending) return <PageLoader />;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold text-darkergray mb-6">
        Gemini Validation Logs
      </h1>

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 mb-6">
          <StatCard label="Total" value={stats.totals?.total || 0} />
          <StatCard
            label="Valid"
            value={stats.totals?.valid || 0}
            color="text-green-700"
          />
          <StatCard
            label="Invalid"
            value={stats.totals?.invalid || 0}
            color="text-red-700"
          />
          <StatCard label="PASS" value={stats.statusCounts?.PASS || 0} />
          <StatCard label="FAIL" value={stats.statusCounts?.FAIL || 0} />
          <StatCard
            label="Parse Err"
            value={stats.statusCounts?.PARSE_ERROR || 0}
          />
          <StatCard label="Error" value={stats.statusCounts?.ERROR || 0} />
          <StatCard
            label="Avg SSIM"
            value={
              stats.totals?.avgSsim == null
                ? "-"
                : formatSsim(stats.totals.avgSsim)
            }
          />
          <StatCard
            label="SSIM-only PASS"
            value={stats.totals?.ssimOnlyPasses || 0}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-mediumgray rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-darkgray mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <FilterField label="Validation Type">
            <SearchSelect
              options={VALIDATION_TYPES}
              value={getOption(VALIDATION_TYPES, filters.validationType)}
              onChange={(opt) =>
                handleFilterChange("validationType", opt?.value || "")
              }
              placeholder="All Types"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Status">
            <SearchSelect
              options={STATUSES}
              value={getOption(STATUSES, filters.status)}
              onChange={(opt) => handleFilterChange("status", opt?.value || "")}
              placeholder="All Statuses"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Valid">
            <SearchSelect
              options={VALID_OPTIONS}
              value={getOption(VALID_OPTIONS, filters.valid)}
              onChange={(opt) => handleFilterChange("valid", opt?.value || "")}
              placeholder="All"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Revision">
            <SearchSelect
              options={REVISION_OPTIONS}
              value={getOption(REVISION_OPTIONS, filters.isRevision)}
              onChange={(opt) =>
                handleFilterChange("isRevision", opt?.value || "")
              }
              placeholder="All"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Search">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="Property, room, violation, notes…"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </FilterField>

          <FilterField label="Category">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="e.g. KITCHEN"
              value={filters.category}
              onChange={(e) => handleFilterChange("category", e.target.value)}
            />
          </FilterField>

          <FilterField label="Model">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="e.g. ssim-api / gemini-3-flash-preview"
              value={filters.model}
              onChange={(e) => handleFilterChange("model", e.target.value)}
            />
          </FilterField>

          <FilterField label="Date Range">
            <SearchSelect
              options={TIME_FRAMES}
              value={getOption(TIME_FRAMES, filters.timeFrame)}
              onChange={(opt) =>
                handleFilterChange("timeFrame", opt?.value || "")
              }
              placeholder="All Time"
              searchable={false}
            />
          </FilterField>

          {isCustomRange && (
            <>
              <FilterField label="Start Date">
                <input
                  className="form-input border border-mediumgray w-full"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </FilterField>
              <FilterField label="End Date">
                <input
                  className="form-input border border-mediumgray w-full"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </FilterField>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button className="gray-button" onClick={clearFilters}>
            Clear
          </Button>
          <Button className="black-button" onClick={applyFilters}>
            Apply Filters
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        title={isFetching ? "Validation Logs (loading…)" : "Validation Logs"}
        columns={columns}
        data={logs || []}
        pageSize={PAGE_SIZE}
        searchable={false}
        serverPagination={
          pagination
            ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                limit: pagination.limit,
              }
            : null
        }
        onPageChange={setCurrentPage}
        onRowClick={(row) => setSelectedLog(row)}
        emptyText="No logs found."
      />

      <LogDetailModal
        log={selectedLog}
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
};

const FilterField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-darkergray mb-1">
      {label}
    </label>
    {children}
  </div>
);

const StatCard = ({ label, value, color = "text-darkergray" }) => (
  <div className="bg-white border border-mediumgray rounded-lg p-3 text-center">
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-darkgray uppercase tracking-wide">{label}</div>
  </div>
);

const LogDetailModal = ({ log, isOpen, onClose }) => {
  if (!log) return null;

  const hasImages = log.originalImageUrl || log.editedImageUrl;
  const violations = Array.isArray(log.violations) ? log.violations : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Validation Log Detail"
      className="max-w-5xl"
    >
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DetailRow label="Created" value={formatLocalDateTime(log.createdAt)} />
          <DetailRow label="Validation Type" value={log.validationType} />
          <DetailRow label="Status" value={log.status} />
          <DetailRow label="Valid" value={log.valid ? "Yes" : "No"} />
          <DetailRow label="Model" value={log.model || "-"} />
          <DetailRow label="SSIM" value={formatSsim(log.ssim)} />
          <DetailRow
            label="Vision Calls"
            value={
              log.visionCalls === null || log.visionCalls === undefined
                ? "-"
                : log.visionCalls === 0
                  ? "0 (SSIM-only)"
                  : String(log.visionCalls)
            }
          />
          <DetailRow label="Room Type" value={log.roomType || "-"} />
          <DetailRow
            label="Duration"
            value={log.durationMs ? `${log.durationMs} ms` : "-"}
          />
          <DetailRow
            label="Attempt"
            value={
              log.attempt
                ? `${log.attempt}${log.maxRetries ? ` / ${log.maxRetries}` : ""}`
                : "-"
            }
          />
          <DetailRow label="Category" value={log.category || "-"} />
          <DetailRow label="Enhancement" value={log.enhancementType || "-"} />
          <DetailRow
            label="Is Revision"
            value={
              log.isRevision === null || log.isRevision === undefined
                ? "-"
                : log.isRevision
                  ? "Yes"
                  : "No"
            }
          />
          <DetailRow label="Image ID" value={log.imageId ?? "-"} />
          <DetailRow
            label="Property"
            value={
              log.propertyAddress ||
              log.propertyId?.address ||
              log.propertyId?._id ||
              "-"
            }
          />
          <DetailRow label="Event ID" value={log.eventId || "-"} />
          <DetailRow label="Revision ID" value={log.revisionId || "-"} />
          <DetailRow
            label="Organization Style ID"
            value={log.organizationStyleId || "-"}
          />
          <DetailRow label="Style ID" value={log.styleId || "-"} />
        </div>

        {violations.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-2">
              Violations ({violations.length})
            </div>
            <ul className="space-y-2">
              {violations.map((violation, index) => {
                const description =
                  violation?.description ||
                  violation?.d ||
                  (typeof violation === "string" ? violation : JSON.stringify(violation));
                const severity = violation?.severity || violation?.s || null;
                return (
                  <li
                    key={`${description}-${index}`}
                    className="bg-red-50 border border-red-200 rounded p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-red-800 break-words">{description}</span>
                      {severity && (
                        <span className="shrink-0 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800 uppercase">
                          {severity}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {log.revisionNotes && (
          <DetailBlock label="Revision Notes" value={log.revisionNotes} />
        )}

        {log.errorMessage && (
          <DetailBlock
            label="Error Message"
            value={log.errorMessage}
            valueClass="text-red-700"
          />
        )}

        {log.parseError && (
          <DetailBlock
            label="Parse Error"
            value={log.parseError}
            valueClass="text-orange-700"
          />
        )}

        {log.rawResponseText && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
              Raw Response Text
            </div>
            <pre className="bg-lightgray border border-mediumgray rounded p-3 text-xs whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
              {log.rawResponseText}
            </pre>
          </div>
        )}

        {hasImages && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-2">
              Image Comparison
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImagePanel label="Original" url={log.originalImageUrl} />
              <ImagePanel label="Edited" url={log.editedImageUrl} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase tracking-wide text-darkgray">
      {label}
    </span>
    <span className="text-darkergray break-words">{String(value ?? "-")}</span>
  </div>
);

const DetailBlock = ({ label, value, valueClass = "" }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
      {label}
    </div>
    <div
      className={`bg-lightgray border border-mediumgray rounded p-3 text-sm whitespace-pre-wrap break-words ${valueClass}`}
    >
      {value}
    </div>
  </div>
);

const ImagePanel = ({ label, url }) => (
  <div>
    <div className="text-xs text-darkgray mb-1">{label}</div>
    {url ? (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={label}
          className="w-full h-auto border border-mediumgray rounded object-contain max-h-96"
        />
      </a>
    ) : (
      <div className="border border-dashed border-mediumgray rounded p-6 text-center text-xs text-darkgray">
        No image
      </div>
    )}
  </div>
);

export default GeminiValidationLogs;
